import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../../components/base/PageLayout';
const Speech: React.FC = () => {
  React.useEffect(() => {
    document.title = '言葉遣いと話し方 - 執事直伝の敬語・声のトーン・話術の極意｜執事ベル';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが教える上品な言葉遣いと話し方。正しい敬語の使い方、適切な声のトーンとスピード、相手に好印象を与える話術、避けるべき言い回しなど、言葉のプロフェッショナルである執事ならではのノウハウを解説。電話応対、プレゼンテーション、接客トークなど、ビジネスシーンで信頼を得るコミュニケーションスキルを身につけたい方必見の内容です。');
    }
  }, []);


  return (
    <PageLayout hero={<>
<div className="relative h-96 bg-gradient-to-r from-gray-900 to-gray-700 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
            style={{
              backgroundImage: `url('/images/generated/seq-speech-hero-de0082.jpg')`
            }}
          />
          <div className="relative z-10 text-center text-white max-w-6xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-serif mb-6 tracking-wide">
              言葉遣いと話し方
            </h1>
            <p className="text-xl md:text-2xl font-light italic opacity-90">
              The Art of Eloquent Communication
            </p>
          </div>
        </div>
</>}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        

        {/* Article Content */}
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12 p-8 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border-l-4 border-amber-600">
              <p className="text-lg leading-relaxed text-gray-800 mb-0">
                言葉は、執事にとって最も重要な道具の一つです。美しい言葉遣いと品格ある話し方は、お客様との信頼関係を築き、プロフェッショナルとしての品位を表現する手段となります。単に正しい敬語を使うだけでなく、相手の心に響く温かみのある言葉を選び、適切なトーンで伝える技術を身につけることが重要です。
              </p>
            </div>

            {/* Main Content */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  言葉の力と責任
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      執事の言葉は、単なる情報伝達の手段ではありません。それは、お客様の心に安らぎを与え、信頼を築き、時には励ましや慰めを提供する力を持っています。だからこそ、言葉の選択と使い方には細心の注意を払う必要があります。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      美しい言葉遣いは、話し手の教養と品格を表現するだけでなく、聞き手への敬意と思いやりを示します。一つ一つの言葉に心を込めて話すことで、真のコミュニケーションが生まれるのです。
                    </p>
                  </div>
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-speech-power-3b4549.jpg"
                      alt="言葉の力"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">言葉遣いの三原則</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-heart-line text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">敬意と思いやり</h4>
                      <p className="text-gray-700 text-sm">相手への深い敬意を込めた言葉選び</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-message-line text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">明確性と正確性</h4>
                      <p className="text-gray-700 text-sm">誤解のない明確な表現</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-music-line text-2xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">美しい響き</h4>
                      <p className="text-gray-700 text-sm">耳に心地よい音調とリズム</p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  敬語の正しい使い方
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  敬語は、相手への敬意を表現する日本語の美しい特徴です。執事として、正しい敬語を自然に使いこなすことで、プロフェッショナルとしての品格を示すことができます。
                </p>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-blue-800 mb-4">
                      <i className="ri-user-star-line text-blue-600 mr-2"></i>
                      尊敬語の使い方
                    </h3>
                    <p className="text-blue-700 mb-4">相手の行動や状態を高めて表現する敬語です。お客様の行動について話す際に使用します。</p>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-blue-800 mb-3">基本的な尊敬語</h4>
                        <ul className="space-y-2 text-blue-700">
                          <li>• いらっしゃる（いる・来る・行く）</li>
                          <li>• おっしゃる（言う）</li>
                          <li>• お召し上がりになる（食べる・飲む）</li>
                          <li>• ご覧になる（見る）</li>
                          <li>• お休みになる（寝る）</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-800 mb-3">使用例</h4>
                        <ul className="space-y-2 text-blue-700">
                          <li>• 「お客様がいらっしゃいました」</li>
                          <li>• 「何とおっしゃいましたか」</li>
                          <li>• 「お茶をお召し上がりください」</li>
                          <li>• 「資料をご覧ください」</li>
                          <li>• 「ごゆっくりお休みください」</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-green-800 mb-4">
                      <i className="ri-user-heart-line text-green-600 mr-2"></i>
                      謙譲語の使い方
                    </h3>
                    <p className="text-green-700 mb-4">自分の行動を低めて表現することで、相手への敬意を示す敬語です。</p>
                    <div className="space-y-4">
                      <div className="bg-white border border-green-300 rounded p-4">
                        <h4 className="font-semibold text-green-800 mb-2">謙譲語の基本形</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <ul className="space-y-1 text-green-700">
                            <li>• 伺う（行く・聞く・訪問する）</li>
                            <li>• 申し上げる（言う）</li>
                            <li>• いただく（もらう・食べる・飲む）</li>
                            <li>• 拝見する（見る）</li>
                          </ul>
                          <ul className="space-y-1 text-green-700">
                            <li>• お手伝いする（手伝う）</li>
                            <li>• ご案内する（案内する）</li>
                            <li>• お預かりする（預かる）</li>
                            <li>• ご用意する（用意する）</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  声のトーンと話し方
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div className="relative">
                    <img 
                      src="/images/generated/seq-speech-tone-5e048b.jpg"
                      alt="声のトーンと話し方"
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      美しい言葉遣いも、適切な声のトーンで話されなければ、その効果は半減してしまいます。執事の声は、安心感を与え、信頼を築く重要な要素です。
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      声の高さ、速度、音量、そして間の取り方を意識することで、言葉に込めた思いを正確に相手に伝えることができます。
                    </p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">理想的な話し方の要素</h3>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 text-amber-600">声の質</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• 適度な低さで安定感を演出</li>
                        <li>• 明瞭な発音で聞き取りやすく</li>
                        <li>• 温かみのある響きを心がける</li>
                        <li>• 疲労感を感じさせない声量</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 text-amber-600">話すリズム</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• ゆっくりと落ち着いたペース</li>
                        <li>• 適切な間を取る</li>
                        <li>• 重要な部分は強調する</li>
                        <li>• 相手のペースに合わせる</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  場面別の言葉遣い
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  執事の仕事では、様々な場面で適切な言葉遣いが求められます。状況に応じて最適な表現を選択することで、プロフェッショナルとしての対応力を示すことができます。
                </p>
                
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      <i className="ri-door-open-line text-amber-600 mr-2"></i>
                      お出迎え・お見送り
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">お出迎えの言葉</h4>
                        <ul className="space-y-1 text-gray-700 text-sm">
                          <li>• 「いらっしゃいませ。お待ちしておりました」</li>
                          <li>• 「本日はお忙しい中、ありがとうございます」</li>
                          <li>• 「お疲れさまでございました」</li>
                          <li>• 「こちらへどうぞ」</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">お見送りの言葉</h4>
                        <ul className="space-y-1 text-gray-700 text-sm">
                          <li>• 「本日はありがとうございました」</li>
                          <li>• 「お気をつけてお帰りください」</li>
                          <li>• 「またのお越しをお待ちしております」</li>
                          <li>• 「失礼いたします」</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      <i className="ri-phone-line text-amber-600 mr-2"></i>
                      電話応対
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-gray-50 border border-gray-200 rounded p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">電話を受ける時</h4>
                        <p className="text-gray-700 text-sm mb-2">「はい、○○でございます。いつもお世話になっております」</p>
                        <p className="text-gray-700 text-sm">「恐れ入ります。少々お待ちください」</p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">電話をかける時</h4>
                        <p className="text-gray-700 text-sm mb-2">「お忙しいところ恐れ入ります。○○の件でお電話いたしました」</p>
                        <p className="text-gray-700 text-sm">「お時間をいただき、ありがとうございました」</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  クッション言葉の活用
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  クッション言葉は、相手への配慮を示し、言葉の印象を和らげる効果があります。執事として、様々な場面で適切なクッション言葉を使いこなすことで、より丁寧で心地よいコミュニケーションが実現できます。
                </p>
                
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-blue-800 mb-4">依頼・お願いの場面</h3>
                    <ul className="space-y-2 text-blue-700">
                      <li>• 「恐れ入りますが」</li>
                      <li>• 「お忙しいところ申し訳ございませんが」</li>
                      <li>• 「もしよろしければ」</li>
                      <li>• 「お手数をおかけしますが」</li>
                      <li>• 「ご都合がよろしい時に」</li>
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-green-800 mb-4">断り・谢罪の場面</h3>
                    <ul className="space-y-2 text-green-700">
                      <li>• 「申し訳ございませんが」</li>
                      <li>• 「あいにくですが」</li>
                      <li>• 「心苦しいのですが」</li>
                      <li>• 「せっかくですが」</li>
                      <li>• 「ご期待に添えず恐縮ですが」</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-serif text-gray-900 mb-6 border-b-2 border-amber-600 pb-3">
                  継続的な向上のために
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  美しい言葉遣いと話し方は、一度身につけたら終わりではありません。常に学び続け、実践を重ねることで、より洗練された表現力を身につけることができます。
                </p>
                
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-lg mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">日常的な練習方法</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-book-open-line text-xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">読書習慣</h4>
                      <p className="text-gray-700 text-sm">美しい文章に触れ、語彙を豊かにする</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-mic-line text-xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">音読練習</h4>
                      <p className="text-gray-700 text-sm">正しい発音と美しい響きを身につける</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-amber-700 text-white rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-record-circle-line text-xl"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">録音確認</h4>
                      <p className="text-gray-700 text-sm">自分の話し方を客観的にチェック</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-amber-800 mb-3">
                    <i className="ri-lightbulb-line mr-2"></i>
                    執事ベルからのアドバイス
                  </h3>
                  <p className="text-amber-700 leading-relaxed">
                    「美しい言葉遣いの根底にあるのは、相手への深い思いやりです。正しい敬語や技術的なスキルも重要ですが、最も大切なのは『この方に喜んでいただきたい』という真心です。その気持ちがあれば、自然と言葉に温かみが宿り、相手の心に響く表現ができるようになります。毎日の会話を大切にし、一つ一つの言葉に心を込めて話すことから始めましょう。」
                  </p>
                </div>
              </section>
            </div>

            {/* Related Articles */}
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h3 className="text-2xl font-serif text-gray-900 mb-8">関連記事</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <a href="/wisdom/articles/communication" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-comm2-92f893.jpg"
                    alt="コミュニケーション術"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">コミュニケーション術</h4>
                    <p className="text-sm text-gray-600 mt-2">心に響く会話の技術</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/posture" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-posture2-45bf66.jpg"
                    alt="美しい所作"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">美しい所作</h4>
                    <p className="text-sm text-gray-600 mt-2">品格ある立ち居振る舞い</p>
                  </div>
                </a>
                
                <a href="/wisdom/articles/social-etiquette" className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img 
                    src="/images/generated/seq-related-social2-48a190.jpg"
                    alt="社交のエチケット"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">社交のエチケット</h4>
                    <p className="text-sm text-gray-600 mt-2">社交の場での振る舞い</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Speech;